import { inject, Injectable } from '@angular/core';
import { handleError } from '../../utils/handle-error';
import {ComponentStore, OnStoreInit, tapResponse} from '@ngrx/component-store';
import { catchError, debounceTime, map, of, pipe, switchMap, tap, withLatestFrom } from 'rxjs';
import { MapboxResult, SearchApiItem, SearchResult, SearchService } from './search.service';
import { filter } from 'rxjs/operators';
import { TMenuItem } from '../../x-kit/components/x-menu/x-menu.component';
import { SearchDictsService } from './search-dicts.service';
import { AppStore } from '../../../stores/app-store';
import {MetricService} from "../../../core/services/metric.service";
import {UserJourneyEvent} from "../../../core/services/user-journey.types";

export interface SearchState {
  searchString: string;
  inputFocused: boolean;
  isSearchStarted: boolean;

  perareResults: SearchResult<SearchApiItem>[];
  isPerareResultsLoading: boolean;
  isPerareResultsError: boolean;

  mapboxResults: MapboxResult[];
  isMapboxResultsLoading: boolean;
  isMapboxResultsError: boolean;

  isNibSearchActive: boolean;
  selectedKabupaten: string;
  isDesaListLoading: boolean;
  desaList: TMenuItem[];

  isKabupatenListLoading: boolean;
  kabupatenList: TMenuItem[];

  selectedDesa: string;
  selectedUsage: string;
  nibSearchResults: SearchResult<SearchApiItem>[];
}

const initialState: SearchState = {
  searchString: '',
  inputFocused: false,
  isSearchStarted: false,

  perareResults: [],
  isPerareResultsLoading: false,
  isPerareResultsError: false,

  mapboxResults: [],
  isMapboxResultsLoading: false,
  isMapboxResultsError: false,

  isNibSearchActive: false,
  selectedKabupaten: '',
  isDesaListLoading: false,
  desaList: [],

  isKabupatenListLoading: false,
  kabupatenList: [],

  selectedDesa: '',
  selectedUsage: '',

  nibSearchResults: [],
};

@Injectable()
export class SearchStore extends ComponentStore<SearchState> implements OnStoreInit {
  private searchService = inject(SearchService);
  private searchDictService = inject(SearchDictsService);
  private appStore = inject(AppStore);
  private metricService = inject(MetricService);

  constructor() {
    super(initialState);
  }

  ngrxOnStoreInit() {
    this.requestKabupatenListByString('');
  }

  public readonly viewModel$ = this.select(
    (state) => {
      return state;
    },
    { debounce: true },
  );
  public clearStateResults = this.updater<void>((state) => {
    return {
      ...state,
      mapboxResults: [],
      perareResults: [],
      isMapboxResultsError: false,
      isPerareResultsError: false,
      isDesaListLoading: false,
      isKabupatenListLoading: false,
      isMapboxResultsLoading: false,
      selectedUsage: '',
      selectedDesa: '',
      selectedKabupaten: '',
      desaList: [],
    };
  });
  public setSearchString = this.updater<string>((state, searchStr) => {
    const safeSearchStr = searchStr ?? '';
    const isNibSearchActive = this.isNibSearch(safeSearchStr);

    if (safeSearchStr === '') {
      this.clearStateResults();
    }

    return {
      ...state,
      searchString: safeSearchStr,
      isSearchStarted: true,
      isNibSearchActive,
      selectedKabupaten: isNibSearchActive ? state.selectedKabupaten : '',
      selectedDesa: isNibSearchActive ? state.selectedDesa : '',
      selectedUsage: isNibSearchActive ? state.selectedUsage : '',
    };
  });

  public setInputFocused = this.updater<boolean>((state, value) => {
    return {
      ...state,
      inputFocused: value,
    };
  });

  public forceSearch = this.effect<void>(
    pipe(
      tap(() => {
        this.clearStateResults();
        this.loadPerareSearchResults();
        this.loadMapboxSearchResults();
      }),
    ),
  );

  public onInputChange = this.effect<string>((searchString$) => {
    return searchString$.pipe(
      map(searchStr => searchStr ?? ''),
      tap((searchStr) => {
        this.setSearchString(searchStr);
        this.metricService.track({name: UserJourneyEvent.USE_SEARCH});
      }),
      debounceTime(500),
      tap((searchStr) => {
        const state = this.get();

        if (!state.isNibSearchActive) {
          this.clearStateResults();
          this.loadPerareSearchResults();
          this.loadMapboxSearchResults();
        } else {
          if (state.selectedUsage && state.selectedDesa && state.selectedKabupaten) {
            this.clearStateResults();
            this.loadNibResults();
          }
        }
      }),

      catchError((error: any) => {
        handleError(error, { source: 'search-store', operation: 'search-input' });
        this.patchState({ 
          isMapboxResultsError: true,
          isPerareResultsError: true 
        });
        return of('');
      })
    );
  });

  private loadMapboxSearchResults = this.effect<void>((searchString$) => {
    return searchString$.pipe(
      map(() => this.get().searchString),
      filter((searchString) => {
        if (searchString === '') {
          return false;
        }

        return true;
      }),
      withLatestFrom(
        of({
          currentPosition: this.appStore?.tempMap?.getCenter()?.toArray() || [
            '106.816666',
            '-6.200000',
          ],
        }),
      ),
      tap(() => {
        this.patchState({ isMapboxResultsLoading: true });
      }),
      switchMap(([str, mapState]) => {
        return this.searchService
          .searchByMapbox(str, {
            lng: String(mapState.currentPosition[0]),
            lat: String(mapState.currentPosition[1]),
          })
          .pipe(
            tapResponse(
              (res) => {
                this.patchState({
                  mapboxResults: res,
                  isMapboxResultsLoading: false,
                });
              },
              (err) => {
                this.patchState({
                  isMapboxResultsLoading: false,
                  isMapboxResultsError: true,
                });
              },
            ),
          );
      }),
    );
  });
  private loadPerareSearchResults = this.effect<void>((searchString$) => {
    return searchString$.pipe(
      map(() => this.get().searchString),
      filter((searchString) => {
        if (searchString === '') {
          return false;
        }

        return true;
      }),
      tap(() => {
        this.patchState({ isPerareResultsLoading: true });
      }),
      switchMap((str) => {
        return this.searchService.searchByString(str).pipe(
          tapResponse(
            (res) => {
              this.patchState({
                perareResults: res,
                isPerareResultsLoading: false,
              });
            },
            (err) => {
              this.patchState({
                isPerareResultsError: true,
                isPerareResultsLoading: false,
              });
            },
          ),
        );
      }),
    );
  });

  public requestDesaListByString = this.effect<string | undefined>((searchString$) =>
    searchString$.pipe(
      tap(() => {
        this.patchState({ isDesaListLoading: true });
      }),
      switchMap((str) => {
        const state = this.get();

        return this.searchDictService.getDesaList(state.selectedKabupaten, str).pipe(
          tapResponse(
            (res) => {
              this.patchState({
                desaList: res,
                isDesaListLoading: false,
              });
            },
            (err) => {
              this.patchState({
                isDesaListLoading: false,
              });
            },
          ),
        );
      }),
    ),
  );

  private requestKabupatenListByString = this.effect<string | undefined>((searchString$) =>
    searchString$.pipe(
      tap(() => {
        this.patchState({ isKabupatenListLoading: true });
      }),
      switchMap((str) => {
        return this.searchDictService.getKabupatenList(str).pipe(
          tapResponse(
            (res) => {
              this.patchState({
                kabupatenList: res,
                isKabupatenListLoading: false,
              });
            },
            (err) => {
              this.patchState({
                isKabupatenListLoading: false,
              });
            },
          ),
        );
      }),
    ),
  );

  private checkForNibRequestReady() {
    const state = this.get();

    if (state.selectedKabupaten && state.selectedDesa && state.selectedUsage) {
      this.loadNibResults();
    }
  }

  private loadNibResults = this.effect<void>(
    pipe(
      tap(() => {
        this.patchState({ isPerareResultsLoading: true });
      }),
      map(() => {
        const state = this.get();
        return [
          state.searchString,
          {
            desa: state.selectedDesa,
            kabupaten: state.selectedKabupaten,
            ownershipType: String(+state.selectedUsage + 1),
          },
        ] as [string, { desa: string; kabupaten: string; ownershipType: string }];
      }),
      switchMap(
        ([str, args]: [string, { desa: string; kabupaten: string; ownershipType: string }]) => {
          return this.searchService.searchByNib(str, args).pipe(
            tapResponse(
              (res) => {
                this.patchState({
                  perareResults: res,
                  isPerareResultsLoading: false,
                });
              },
              (err) => {
                this.patchState({
                  isPerareResultsLoading: false,
                  isPerareResultsError: true,
                });
              },
            ),
          );
        },
      ),
    ),
  );

  public onKabupatenSelect = this.effect<string>((id$) => {
    return id$.pipe(
      tap((kabId) => {
        const currentKabupatenId = this.get().selectedKabupaten;

        if (kabId !== currentKabupatenId) {
          this.patchState({ selectedKabupaten: kabId, selectedDesa: '' });
          this.requestDesaListByString(undefined);
        }
      }),
      tap(() => {
        this.checkForNibRequestReady();
      }),
    );
  });

  public updateKabupatenList = this.effect<string>((searchStr$) => {
    return searchStr$.pipe(
      debounceTime(400),
      tap((str) => {
        this.requestKabupatenListByString(str)
      }),
    );
  });

  public onOwnershipSelect = this.effect<string>((id$) => {
    return id$.pipe(
      tap((usageId) => {
        this.patchState({ selectedUsage: usageId });
      }),
      tap(() => {
        this.checkForNibRequestReady();
      }),
    );
  });

  public onDesaSelect = this.effect<string>((id$) => {
    return id$.pipe(
      tap((usageId) => {
        this.patchState({ selectedDesa: usageId });
      }),
      tap(() => {
        this.checkForNibRequestReady();
      }),
    );
  });

  private isNibSearch(term: string): boolean {
    const isValidNib = /^\d+$/.test(term);
    const isValidLength = term.length >= 3 && term.length <= 5;

    return isValidNib && isValidLength;
  }
}
